import { flushSync } from 'react-dom';

/** Lets a light table force its subtree's upgrade when the page needs the full content now. */
export type LightTableControls = {
	activateNow: () => void;
	isPreloaded: () => boolean;
	isWhollySelected: (selection: Selection) => boolean;
	prepareNow: () => void;
};

// Tables in one subtree share their controls; count them so the subtree stays registered until all
// of its tables unmount.
const subtrees = new Map<LightTableControls, number>();

// Copy would serialize text rows as flat text, so light tables upgrade first, synchronously.
// An upgrade replaces the table's whole top-level subtree. When the selection contains the whole
// subtree, both boundaries sit outside the replaced DOM and the full content lands inside the range.
// When it is partly selected, a boundary inside the replaced DOM would move to its parent and
// collapse or shrink the selection, so the subtree stays light and copies its text rows. That only
// affects tables that were not upgraded before the selection started; visible tables usually were.
// A table whose preloads have not succeeded also stays light: upgrading it would copy loading states.
const onCopy = () => {
	const selection = window.getSelection();
	if (!selection || selection.isCollapsed) {
		return;
	}
	const selected = [...subtrees.keys()].filter(
		(controls) => controls.isPreloaded() && controls.isWhollySelected(selection),
	);
	if (selected.length) {
		flushSync(() => selected.forEach((controls) => controls.activateNow()));
	}
};

// Copy cannot wait for chunks, so preload once a selection starts, by drag or select-all. The
// tables share a few full components, so preloading them all costs little more than one, and
// selectstart fires once per selection rather than on every selection change.
const onSelectStart = () => {
	subtrees.forEach((_count, controls) => {
		if (!controls.isPreloaded()) {
			controls.prepareNow();
		}
	});
};

export function registerLightTable(controls: LightTableControls): () => void {
	if (!subtrees.size && typeof document !== 'undefined') {
		document.addEventListener('copy', onCopy, true);
		document.addEventListener('selectstart', onSelectStart);
	}
	subtrees.set(controls, (subtrees.get(controls) ?? 0) + 1);
	return () => {
		const count = (subtrees.get(controls) ?? 1) - 1;
		if (count > 0) {
			subtrees.set(controls, count);
		} else {
			subtrees.delete(controls);
		}
		if (!subtrees.size && typeof document !== 'undefined') {
			document.removeEventListener('copy', onCopy, true);
			document.removeEventListener('selectstart', onSelectStart);
		}
	};
}
